import { Seo } from "../components/Seo";
import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import api, { formatApiError } from "../lib/api";
import { Link } from "react-router-dom";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_interest: "", message: "", sms_consent: false });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent!");
      setForm({ name: "", email: "", phone: "", service_interest: "", message: "", sms_consent: false });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo title="Contact & Location — Clinton Township, MI" description="Visit Iridescent Medical & Wellness Spa at 42855 Garfield Rd, Suite 105, Clinton Township, MI 48038. Call (586) 690-8693 to book your appointment." path="/contact" />
      <PageHeader
        overline="Contact & Location"
        title="Come visit us in Michigan"
        subtitle="Questions about a treatment or ready to plan your visit? Reach out — our team would love to help."
      />

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12">
          <Reveal>
            <form onSubmit={submit} className="bg-white rounded-[2rem] p-8 md:p-10 border border-slate-100 space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="text-ink">Name</Label>
                  <Input id="name" required value={form.name} onChange={update("name")} data-testid="contact-name" className="mt-2 rounded-lg" placeholder="Your name" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-ink">Phone</Label>
                  <Input id="phone" value={form.phone} onChange={update("phone")} data-testid="contact-phone" className="mt-2 rounded-lg" placeholder="(586) 555-0000" />
                </div>
              </div>
              <div>
                <Label htmlFor="email" className="text-ink">Email</Label>
                <Input id="email" type="email" required value={form.email} onChange={update("email")} data-testid="contact-email" className="mt-2 rounded-lg" placeholder="you@email.com" />
              </div>
              <div>
                <Label htmlFor="service" className="text-ink">Treatment of interest</Label>
                <Input id="service" value={form.service_interest} onChange={update("service_interest")} data-testid="contact-service" className="mt-2 rounded-lg" placeholder="e.g. HydraFacial, Botox" />
              </div>
              <div>
                <Label htmlFor="message" className="text-ink">Message</Label>
                <Textarea id="message" required value={form.message} onChange={update("message")} data-testid="contact-message" className="mt-2 rounded-lg min-h-32" placeholder="How can we help?" />
              </div>
              <div className="rounded-xl bg-stone p-4 space-y-3" data-testid="sms-consent-section">
                <div className="flex items-center gap-2">
                  <p className="font-heading text-ink text-sm font-medium">Text message updates</p>
                  <span className="text-[10px] uppercase tracking-wide font-medium text-[#8A9492] bg-white/70 border border-slate-200 rounded-full px-2 py-0.5" data-testid="sms-consent-optional-badge">Optional</span>
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.sms_consent}
                    onChange={(e) => setForm({ ...form, sms_consent: e.target.checked })}
                    data-testid="contact-sms-consent"
                    className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-iri-mid focus:ring-iri-mid" />
                  <span className="text-sm text-[#5C6664] leading-relaxed">
                    Yes, I would like to receive automated text messages from <strong>Cody Wallace Enterprises</strong> (Iridescent Medical &amp; Wellness Spa) about appointment activity, promotional offers, and important updates. I understand I will receive up to 4 messages per month.
                  </span>
                </label>
                <p className="text-xs text-[#8A9492] leading-relaxed">
                  <strong className="text-ink">Message Frequency:</strong> You will receive up to 4 messages per month.<br />
                  <strong className="text-ink">Standard Rates:</strong> Message and data rates may apply depending on your mobile phone service plan.<br />
                  <strong className="text-ink">Help &amp; Stop:</strong> Reply HELP for help or STOP to cancel at any time. By providing your phone number and checking the box above, you agree to receive text messages from Cody Wallace Enterprises. Consent is not required to make a purchase.
                </p>
                <p className="text-xs">
                  <Link to="/terms" className="text-iri-mid hover:text-ink underline" data-testid="link-terms">Terms of Service</Link>
                  <span className="text-[#8A9492] mx-1">|</span>
                  <Link to="/privacy" className="text-iri-mid hover:text-ink underline" data-testid="link-privacy">Privacy Policy</Link>
                </p>
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="iri-gradient text-ink rounded-full h-12 px-8 font-medium hover:scale-[1.02] transition-transform w-full sm:w-auto"
              >
                {loading ? "Sending…" : (<>Send message <Send className="ml-2 h-4 w-4" /></>)}
              </Button>
            </form>
          </Reveal>

          <Reveal delay={0.1} className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                [MapPin, "Location", "42855 Garfield Rd, Suite 105\nClinton Twp, MI 48038"],
                [Phone, "Call us", "586.690.8693"],
                [Mail, "Email", "hello@iridescent-spa.com"],
                [Clock, "Hours", "Tue & Fri 10–7 · Wed & Thu 10–5\nSat 10–7 · Sun & Mon closed"],
              ].map(([Icon, title, val]) => (
                <div key={title} className="bg-white rounded-2xl p-6 border border-slate-100">
                  <Icon className="h-5 w-5 text-iri-mid" strokeWidth={1.5} />
                  <p className="font-heading text-ink font-medium mt-3">{title}</p>
                  <p className="text-sm text-[#5C6664] mt-1 whitespace-pre-line">{val}</p>
                </div>
              ))}
            </div>
            <div className="rounded-[2rem] overflow-hidden border border-slate-100 h-72" data-testid="contact-map">
              <iframe
                title="Iridescent Wellness Spa location"
                src="https://www.google.com/maps?q=42855+Garfield+Rd+Suite+105+Clinton+Township+MI+48038&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
